import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription } from 'rxjs';

import { BlogImage, BlogPost } from './blog-post';

/** A block ready for the template: either sanitized markup or an illustration. */
interface RenderedBlock {
  kind: 'html' | 'image';
  html?: SafeHtml;
  image?: BlogImage;
}

@Component({
  selector: 'app-blog-post',
  templateUrl: './blog-post.component.html',
  styleUrls: ['./blog-post.component.scss']
})
export class BlogPostComponent implements OnInit, OnDestroy {

  post: BlogPost = null;
  renderedBlocks: RenderedBlock[] = [];

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
  }

  ngOnInit() {
    // Each post is its own route carrying its content in route data. Subscribing rather
    // than reading the snapshot keeps this correct if the component is ever reused in
    // place instead of being recreated per navigation, matching GuideComponent.
    this.subscriptions.add(this.route.data.subscribe(data => {
      this.post = data.post;
      this.renderedBlocks = (this.post ? this.post.blocks : []).map((block): RenderedBlock =>
        block.kind === 'image'
          ? { kind: 'image', image: block.image }
          // The markup was rendered from markdown that lives in this repository and was
          // compiled at build time — it is never user input — so it is trusted verbatim
          // rather than run through Angular's sanitizer, which would strip the ids and
          // attributes the rendered content relies on. Mapped once here rather than in
          // the template so change detection is not handed a new value every cycle.
          : { kind: 'html', html: this.sanitizer.bypassSecurityTrustHtml(block.html) });
    }));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /*
   * Links inside rendered markdown are plain <a href="/pricing"> elements, so without
   * this they would leave the app and reload the whole site. Anything the browser should
   * handle itself - modified clicks, new-tab targets, external and fragment links - is
   * left alone.
   */
  onContentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const copyButton = target && target.closest ? target.closest('.copy-button') as HTMLElement : null;
    if (copyButton) {
      this.copyCodeBlock(copyButton);
      return;
    }

    if (event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const anchor = target && target.closest ? target.closest('a') : null;
    if (!anchor || anchor.target) {
      return;
    }

    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('//')) {
      return;
    }

    event.preventDefault();
    this.router.navigateByUrl(href);
  }

  /*
   * The button lives next to <code> inside .code-block (see
   * scripts/lib/blog-content.js), not as an Angular-bound control - like onContentClick's
   * link handling above, it's raw DOM reached via delegation. .copied drives the
   * copy/check icon swap purely in CSS (blog-post.component.scss).
   */
  private copyCodeBlock(button: HTMLElement) {
    if (!navigator.clipboard) {
      return;
    }
    const code = button.parentElement && button.parentElement.querySelector('code');
    if (!code) {
      return;
    }
    navigator.clipboard.writeText(code.textContent || '').then(() => {
      button.classList.add('copied');
      setTimeout(() => button.classList.remove('copied'), 2000);
    }).catch(() => {});
  }

}
